import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { config } from '@/config/index.js';
import constants from '@/constants/constants.js';
import homePaths from '@/constants/homePaths';
import linesOfBusiness from '@/constants/linesOfBusiness.js';
import socketEvents from '@/constants/socketEvents.js';
import { useKayAuth } from '@/context/AuthContext.tsx';
import { getStatusColor } from '@/helpers/colors.js';
import { countLength, formatTitle } from '@/helpers/helperFunctions';
import { formatDate } from '@/helpers/helperFunctions.ts';
import backendApis from '@/services/backendApis';
import { AgentTaskState, BrowserRun } from '@/types/index.js';
import {
  WorkflowLogStatus,
  WorkflowRunListObject,
  WorkflowState,
} from '@/types/workflowRuns';
import { useAuth } from '@clerk/clerk-react';
import { BadgeCheck, CircleAlert, Clock } from 'lucide-react';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type AllAgentTasksScreenProps = {
  runningOnly?: boolean;
};

const AllAgentApplicationsScreen = forwardRef(
  (props: AllAgentTasksScreenProps, ref) => {
    const { runningOnly = false } = props;
    const { user, workflowId } = useKayAuth();
    const { getToken } = useAuth();
    const navigate = useNavigate();
    const [applications, setApplications] = useState<
      WorkflowRunListObject[] | null
    >([] | null);
    const [browserRuns, setBrowserRuns] = useState<Record<string, BrowserRun>>(
      {},
    );
    const socketRef = useRef<any>();
    const intervalRef = useRef<any>();

    useImperativeHandle(ref, () => ({
      refresh: refreshApplications,
    }));

    const refreshApplications = () => {
      setApplications(null);
      setBrowserRuns({});
      getApplicationsFromDB();
    };

    const getApplicationsFromDB = async () => {
      if (!user || !workflowId) {
        return;
      }
      try {
        const response = await backendApis.listWorkflowRuns({
          workflow_id: workflowId,
        });
        let items: WorkflowRunListObject[] = response.data.items;
        items = items.filter(
          (item: WorkflowRunListObject) =>
            !item.workflow_run.workflow_run_id.startsWith(constants.editorKey),
        );
        const sortedItems = items.sort(
          (a: WorkflowRunListObject, b: WorkflowRunListObject) =>
            new Date(b.workflow_run.created_at).getTime() -
            new Date(a.workflow_run.created_at).getTime(),
        );
        setApplications(sortedItems);
      } catch {
        toast.error('Failed to get applications');
      }
    };

    useEffect(() => {
      if (workflowId) {
        refreshApplications();
      }
    }, [workflowId]);

    const fetchBrowserRuns = async (ids: string[]) => {
      if (!ids) {
        return;
      }
      try {
        const response = await backendApis.getBrowserRunsBatch({
          browser_run_ids: ids,
        });
        const items = response.data?.browser_runs || [];
        const newBrowserRuns = items.reduce((acc: any, val: BrowserRun) => {
          acc[val.browser_run_id] = val;
          return acc;
        }, {});
        setBrowserRuns({ ...browserRuns, ...newBrowserRuns });
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    useEffect(() => {
      if (applications && applications.length > 0) {
        const unprocessedIds = applications
          .flatMap((task) => task.workflow_run.browser_run_ids)
          .filter((id) => !browserRuns[id]);
        fetchBrowserRuns(unprocessedIds);
      }
    }, [applications]);

    const sendWSMessage = (type: string, data: any = {}) => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        data = {
          ...data,
          action: 'message',
          action_type: type,
          payload: data?.payload || {},
        };
        socketRef.current.send(JSON.stringify(data));
      } else {
        console.error('WebSocket not connected');
      }
    };

    const initConnection = async () => {
      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        return;
      }
      const token = await getToken();
      console.log('Initializing WebSocket connection');
      let socketUrl = config.backend.wsUrl;
      socketUrl += `?authentication=${token}`;
      const ws = new WebSocket(socketUrl);
      socketRef.current = ws;

      ws.onopen = () => {
        console.log('Connected to signalling server');
        socketRef.current = ws;

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        intervalRef.current = setInterval(() => {
          sendWSMessage('heartbeat');
        }, 29_000);
      };

      ws.onmessage = async (event) => {
        try {
          const silentEvents = ['heartbeat_response'];
          const parsedData = JSON.parse(event.data);
          const eventType = parsedData.type || parsedData.action_type;
          if (!silentEvents.includes(eventType)) {
            console.log('Received message from server:', eventType, parsedData);
          }

          switch (eventType) {
            case socketEvents.REGISTRATION_SUCCESS:
              break;
            case socketEvents.BrowserRunUpdate:
            case socketEvents.WorkflowRunNameUpdate:
            case socketEvents.WorkflowLogUpdate: {
              refreshApplications();
              break;
            }
            case socketEvents.MESSAGE: {
              const message = parsedData.payload.description;
              console.log('Message received:', message);
              toast.info(message);
              break;
            }
            default:
              console.log('Unhandled Event', parsedData);
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('Disconnected from signalling server');
        setTimeout(initConnection, 2000);
      };
    };

    useEffect(() => {
      initConnection();
    }, []);

    const getStatusBadge = (status: string) => {
      return (
        <Badge className={`${getStatusColor(status)} font-medium`}>
          {formatTitle(status)}
        </Badge>
      );
    };

    const openApplication = (applicationId: string) => {
      const url =
        [homePaths.home, homePaths.hub].join('') + '/' + applicationId;
      navigate(url);
    };

    const getApplicationStatus = (application: WorkflowRunListObject): string => {
      const browserRunIds = application.workflow_run.browser_run_ids;
      const runs = browserRunIds.map((a) => browserRuns[a]).filter(Boolean);
      const allLogStatuses = application?.workflow_run_log_statuses || [];
      const preCheckFailed =
        allLogStatuses.includes(WorkflowLogStatus.PRECHECKLIST_FAILED);
      const userInputReq = runs.some(
        (a) => a.browser_run_state === AgentTaskState.WAITING_FOR_USER_TO_ANSWER,
      );
      if (
        application.workflow_context?.workflow_state === WorkflowState.COMPLETED
      ) {
        return 'completed';
      }
      if (
        application.workflow_context?.workflow_state === WorkflowState.CANCELLED ||
        application.workflow_log?.workflow_log_records.find(
          (b) => b.log_record_status === WorkflowLogStatus.WORKFLOW_CANCELLED,
        )
      ) {
        return 'cancelled';
      }
      if (preCheckFailed || userInputReq) {
        return 'your input required';
      }
      return 'running';
    };

    const renderApplications = (
      application: WorkflowRunListObject,
      status: string,
    ) => {
      const browserRunIds = application.workflow_run.browser_run_ids;
      const runs = browserRunIds.map((a) => browserRuns[a]).filter(Boolean);
      const runIndexZero = runs[0];
      const fetchedRuns = countLength(browserRunIds) === countLength(runs);
      let Lob = runIndexZero?.business_line;
      if (linesOfBusiness.some((a) => a.key === Lob)) {
        Lob = linesOfBusiness.find((a) => a.key === Lob)?.name;
      }
      const portals: string[] | undefined = runs?.map((a) => a.carrier_name);
      const applicationName = application?.workflow_run?.workflow_run_name;

      let showStatus: string;
      if (!runningOnly && status === 'default') {
        showStatus = getApplicationStatus(application);
      } else {
        showStatus = status;
      }

      return (
        <TableRow
          className="hover:cursor-pointer hover:bg-gray-100"
          key={application.workflow_run.workflow_run_id}
          onClick={() =>
            openApplication(application.workflow_run.workflow_run_id)
          }
        >
          <TableCell>
            <span className="text-gray-700">
              {formatDate(application.workflow_run.created_at)}
            </span>
          </TableCell>
          <TableCell>
            <span className="font-medium">{applicationName}</span>
          </TableCell>
          <TableCell>
            <span className="text-gray-700 line-clamp-1 max-w-[250px]">
              {fetchedRuns ? (
                formatTitle(Lob)
              ) : (
                <Skeleton className="h-6 w-12 bg-gray-300" />
              )}
            </span>
          </TableCell>
          <TableCell>
            {fetchedRuns ? (
              <span className="text-gray-700">
                {portals?.map((a) => formatTitle(a)).join(', ')}
              </span>
            ) : (
              <Skeleton className="h-6 w-12 bg-gray-300" />
            )}
          </TableCell>
          <TableCell>{getStatusBadge(showStatus)}</TableCell>
        </TableRow>
      );
    };

    const categorizeTaskSubsections = (
      applications: WorkflowRunListObject[],
    ) => {
      const completedTasks = applications.filter(
        (a) => a.workflow_context?.workflow_state === WorkflowState.COMPLETED,
      );
      const cancelledTasks = applications.filter(
        (a) =>
          a.workflow_context?.workflow_state === WorkflowState.CANCELLED ||
          a?.workflow_run_log_statuses?.includes(
            WorkflowLogStatus.WORKFLOW_CANCELLED
          ),
      );
      const completedCancelledApplicationIds = [
        ...completedTasks,
        ...cancelledTasks,
      ].map((a) => a.workflow_run.workflow_run_id);
      const remainingTasks = applications.filter(
        (a) =>
          !completedCancelledApplicationIds.includes(
            a.workflow_run.workflow_run_id,
          ),
      );

      const needYourInputTasks = remainingTasks.filter((application) => {
        const browserRunIds = application.workflow_run.browser_run_ids;
        const runs = browserRunIds.map((a) => browserRuns[a]).filter(Boolean);
        const allLogStatuses = application?.workflow_run_log_statuses || [];
        const lastStatus = allLogStatuses.at(-1);
        const preCheckFailed =
          lastStatus === WorkflowLogStatus.PRECHECKLIST_FAILED;
        const userInputReq = runs.some(
          (a) =>
            a.browser_run_state === AgentTaskState.WAITING_FOR_USER_TO_ANSWER,
        );
        return userInputReq || preCheckFailed;
      });

      const runningAITasks = remainingTasks.filter(
        (task) => !needYourInputTasks.includes(task),
      );

      const recentlyCompletedTasks = completedTasks
        .sort(
          (a, b) =>
            new Date(b.workflow_run.created_at) -
            new Date(a.workflow_run.created_at),
        )
        .slice(0, 5);

      return {
        needYourInputTasks,
        recentlyCompletedTasks,
        runningAITasks,
      };
    };

    const TaskSection = ({ status, title, tasks, variant }: any) => {
      if (!tasks) {
        return null;
      }
      const variantTextStyles = {
        attention: 'text-orange-800',
        info: 'text-sky-800',
        success: 'text-emerald-800',
      };
      const variantBgStyles = {
        attention: 'bg-orange-100',
        info: 'bg-sky-100',
        success: 'bg-emerald-100',
      };
      const iconClassname = 'h-4 w-4';
      const variantIcon = {
        attention: <CircleAlert className={iconClassname} />,
        info: <Clock className={iconClassname} />,
        success: <BadgeCheck className={iconClassname} />,
      };

      if (countLength(tasks) === 0) {
        return null;
      }

      const cardClasses = `group mb-6 border-2 transition-all duration-200 bg-white
    ${variant === 'attention' ? 'attention-border shadow-lg' : 'shadow-md'}
  `;

      return (
        <Card className={cardClasses}>
          <CardHeader className="pb-3">
            <CardTitle className={`flex items-center space-x-2`}>
              <p className={`${variantTextStyles[variant]}`}>
                {variantIcon[variant]}
              </p>
              <div className="font-medium text-black">{title}</div>
              <Badge
                className={`${variantTextStyles[variant]} ${variantBgStyles[variant]} rounded-full`}
                variant="secondary"
              >
                {tasks.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-hidden rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[110px] text-xs font-medium text-gray-500">
                      Date
                    </TableHead>
                    {['Client Name', 'LOB', 'Carriers', 'Status'].map((a) => (
                      <TableHead
                        className="text-xs font-medium text-gray-500"
                        key={a}
                      >
                        {a}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody className="py-0.5">
                  {tasks.map((task) => renderApplications(task, status))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      );
    };

    if (!applications) {
      return (
        <div className="space-y-3 p-4">
          <Skeleton className="mt-2 h-8 w-3/4 bg-gray-500" />
          <Skeleton className="mt-2 h-10 w-full bg-gray-500" />
          <Skeleton className="mt-2 h-8 w-3/4 bg-gray-500" />
        </div>
      );
    }

    const { needYourInputTasks, recentlyCompletedTasks, runningAITasks } =
      categorizeTaskSubsections(applications);

    if (countLength(applications) === 0) {
      return (
        <div>
          <div className="flex h-48 items-center justify-center">
            <p className="text-lg text-gray-500">No tasks available</p>
          </div>
        </div>
      );
    }

    if (!runningOnly) {
      return (
        <div>
          <TaskSection
            status="default"
            tasks={applications}
            title={`All Applications`}
          />
        </div>
      );
    }

    if (
      countLength(applications) === 0 ||
      (countLength(needYourInputTasks) === 0 &&
        countLength(runningAITasks) === 0 &&
        countLength(recentlyCompletedTasks) === 0)
    ) {
      return (
        <div>
          <div className="flex h-48 items-center justify-center">
            <p className="text-lg text-gray-500">No tasks available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <TaskSection
          status="your input required"
          tasks={needYourInputTasks}
          title="Your input required"
          variant="attention"
        />
        <TaskSection
          status="running"
          tasks={runningAITasks}
          title="In Progress"
          variant="info"
        />
        <TaskSection
          status="completed"
          tasks={recentlyCompletedTasks}
          title="Recently Completed"
          variant="success"
        />
      </div>
    );
  },
);

export default AllAgentApplicationsScreen;
